import {
  FeatureName,
  OrderByInput,
  useAccountListOrder,
  useIsFeatureEnabled,
  useSetAccountListOrder,
} from '@gripp/shared-logic';
import { InfiniteDataGrid, SelectionStatus } from '@gripp/shared-ui';
import React from 'react';
import { StyleSheet, View } from 'react-native';
import { PeopleDetailAccount } from './accountDetailPanel';
import { useColumnsAccount } from './useColumnsAccount';

type AccountListProps = {
  filter: any;
  updateRowsSelected?: (rows: Array<any>) => void;
  isRowSelected?: (row: any) => boolean;
  selectionStatus?: SelectionStatus;
  onSelectionStatusChange?: (status: SelectionStatus) => void;
  showCheckboxColumn: boolean;
  showRolesColumn: boolean;
  showImpersonateWorkspace: boolean;
  showImpersonateUser: boolean;
  showGlobalAdminRole: boolean;
  onItemsCountChange?: (count: number) => void;
  addToGroupsComponent?: JSX.Element;
  workspaceId?: string;
  onRowClick?: (row: PeopleDetailAccount) => void;
};

export const AccountList = ({
  filter,
  updateRowsSelected,
  isRowSelected,
  selectionStatus,
  onSelectionStatusChange,
  showCheckboxColumn,
  showRolesColumn,
  showImpersonateWorkspace,
  showImpersonateUser,
  showGlobalAdminRole,
  onItemsCountChange,
  addToGroupsComponent,
  workspaceId,
  onRowClick,
}: AccountListProps) => {
  const { data: accountOrder } = useAccountListOrder();
  const { mutateAsync: setAccountOrder } = useSetAccountListOrder();
  const isCropCircleEnabled = useIsFeatureEnabled(
    FeatureName.CropCircleRedesign
  );

  const { columns, exportColumns } = useColumnsAccount(
    showCheckboxColumn,
    showRolesColumn,
    showImpersonateWorkspace,
    showImpersonateUser,
    showGlobalAdminRole,
    workspaceId
  );

  const onOrderByChange = async (orderBy: OrderByInput) => {
    await setAccountOrder(orderBy);
  };

  return (
    <View
      style={[
        styles.accountsContainer,
        isCropCircleEnabled && styles.accountsContainerCropCircle,
      ]}
    >
      <InfiniteDataGrid
        columns={columns}
        exportColumns={exportColumns}
        query={FIND_WORKSPACE_ACCOUNTS}
        dataPrefix="account.listAccounts"
        modelName="account"
        exportFileName="people"
        orderBy={accountOrder}
        pageSize={50}
        filter={filter}
        hideHeader={false}
        showScrollIndicator={true}
        updateRowsSelected={updateRowsSelected}
        isRowSelected={isRowSelected}
        selectionStatus={selectionStatus}
        onSelectionStatusChange={onSelectionStatusChange}
        onItemsCountChange={onItemsCountChange}
        onOrderByChange={onOrderByChange}
        headerContent={addToGroupsComponent}
        onRowClick={onRowClick}
        rowTestIdPrefix="account-row"
      />
    </View>
  );
};

const FIND_WORKSPACE_ACCOUNTS = `
  query FindWorkspaceAccounts($filter: ListAccountsFilter, $page: PageRequest, $orderBy: OrderByInput) {
    account {
      listAccounts(filter: $filter, page: $page, orderBy: $orderBy) {
        count
        offset
        limit
        items {
          id
          createdAt
          name {
            first
            last
          }
          email
          phone {
            callingCode,
            countryCode,
            phoneNumber
          }
          location {
            type
            coordinates
          }
          lastScannedAddress
          jobTitle
          groups {
            id
            name
          }
          workspace {
            id
            name
            type
          }
          workspaceRoles {
            workspace
            role
          }
        }
      }
    }
  }
`;

const styles = StyleSheet.create({
  accountsContainer: {
    flex: 1,
    borderRadius: 8,
    overflow: 'hidden',
    zIndex: -1,
  },
  accountsContainerCropCircle: {
    borderRadius: 0,
  },
});
